import { useEffect, useState } from "react";
import { Plus, Trash2, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const ROLES = [
  { value: "super_admin", label: "Super Administrateur" },
  { value: "deposits_service", label: "Service des Dépôts" },
  { value: "library", label: "Bibliothèque" },
  { value: "archives", label: "Archives" },
  { value: "doctoral_school", label: "École Doctorale" },
  { value: "cecom", label: "CECOM" },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "deposits_service" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setForm({ email: "", name: "", role: "deposits_service" });
      if (data.onboarding_sent) {
        toast.success(`Invitation envoyée à ${data.email}. L'utilisateur créera lui-même son mot de passe.`);
      } else {
        toast.warning(`Compte créé mais l'e-mail n'a pas pu partir. Utilisez « Renvoyer l'invitation ».`);
      }
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Invitation renvoyée à ${u.email}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Supprimer définitivement l'utilisateur ${u.email} ?`)) return;
    await api.delete(`/admin/users/${u.id}`);
    load();
  };

  return (
    <div data-testid="admin-users">
      <div className="eyebrow eyebrow-dark">Administration</div>
      <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Utilisateurs</h1>
      <span className="divider-gold mt-3" />

      <div className="mt-8 paper-card p-6" data-testid="user-form-card">
        <div className="flex items-start gap-3 mb-5 text-sm text-slate-600 bg-slate-50/70 border border-slate-200 rounded-sm p-3">
          <MailCheck className="w-4 h-4 text-unisic-gold flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            Créez uniquement l'adresse et le rôle. Un e-mail d'activation est envoyé à
            l'utilisateur — il définit lui-même son mot de passe via un lien sécurisé
            (valable 7 jours, usage unique).
          </p>
        </div>
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-4" data-testid="user-form">
          <input
            required
            placeholder="Nom complet"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
            data-testid="input-user-name"
          />
          <input
            required
            type="email"
            placeholder="E-mail institutionnel"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
            data-testid="input-user-email"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white"
            data-testid="input-user-role"
          >
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button
            className="btn-primary text-sm justify-center"
            type="submit"
            disabled={submitting}
            data-testid="btn-add-user"
          >
            <Plus className="w-4 h-4" />
            {submitting ? "Envoi..." : "Créer et inviter"}
          </button>
          {error && <div className="md:col-span-4 text-sm text-unisic-red">{error}</div>}
        </form>
      </div>

      <div className="mt-6 paper-card overflow-hidden">
        <table className="unisic-table w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Rôle</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-widest bg-unisic-blue/10 text-unisic-blue px-2 py-1 rounded-full">
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.onboarding_pending ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-1 rounded-full"
                      data-testid={`badge-pending-${u.id}`}
                    >
                      <MailCheck className="w-3 h-3" /> Invitation en attente
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                      Actif
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => resend(u)}
                      className="inline-flex items-center gap-1.5 text-xs text-unisic-blue hover:text-unisic-gold"
                      title="Renvoyer un e-mail d'invitation à créer un mot de passe"
                      data-testid={`btn-resend-${u.id}`}
                    >
                      <Send className="w-3.5 h-3.5" />
                      {u.onboarding_pending ? "Renvoyer l'invitation" : "Envoyer un lien de reset"}
                    </button>
                    <button
                      onClick={() => del(u)}
                      className="text-slate-400 hover:text-unisic-red"
                      data-testid={`btn-del-user-${u.id}`}
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
